/**
 * Citation registry — shared contract between CD-v3 backend and the frontend.
 *
 * The assistant cites sources inline via `<hector-link>` tags. Historically the
 * LLM had to transcribe the full target by hand (`url="…"`, `file_path="…"`),
 * which it regularly got wrong (bare `legifrance.gouv.fr`, folder paths, dead
 * links). The registry removes that transcription: each citable resource a tool
 * returns is assigned a short, conversation-scoped **token** (e.g. `piece-12`,
 * `art-2286-cc`, `jp-1`). The LLM only ever emits the token; the code resolves
 * `token → CitationTarget` deterministically. An unknown token (fabricated by
 * the model) resolves to nothing and is rendered as plain text.
 *
 * This file is the wire/persistence contract only — the minting/dedup logic
 * lives in the backend (`services/hector-agent-case-discovery-v3/citations`).
 */

import { z } from 'zod';
import { OrdreSchema } from '../jurisprudence/index.js';

/**
 * A short citation handle emitted by the LLM, unique within one conversation
 * (e.g. `piece-12`, `art-2286-cc`, `jp-1`, `web-3`). Resolved via the
 * conversation's {@link CitationRegistry}.
 */
export type CitationToken = string;

/** Case document (pièce) — opened in the internal document viewer. */
export const PieceCitationSchema = z.object({
  kind: z.literal('piece'),
  /** `case_documents` UUID — the stable key; also embedded in `filePath` when known. */
  documentId: z.string(),
  /**
   * OVH storage key (`cases/{caseId}/{documentId}.ext`). Optional: some sources
   * (e.g. `read_case_wiki`) only expose the `documentId`. When absent, both
   * frontends open the piece by `documentId` instead — legalworkspace resolves
   * the path via `GET /cases/{caseId}/documents` (needs the piece's case: see
   * `caseId`), the add-in calls `POST /api/documents/{id}/signed-url`. So
   * `filePath` is purely an optimisation (skips that lookup) — a piece is
   * always openable by id.
   */
  filePath: z.string().optional(),
  name: z.string(),
  /**
   * `cases` UUID of the dossier the piece belongs to. The agent reads pieces of
   * a related dossier (« connexe ») as well as the principal one, so the piece's
   * dossier is no longer implied by the conversation: without it, legalworkspace
   * would look the piece up in the exchange's dossier and could open a namesake.
   *
   * Optional because registries persisted before this field carry none — the web
   * then falls back to the dossier frozen on the exchange. Declared (not left as
   * an extra key) because these are non-strict Zod objects: an undeclared key is
   * silently stripped every time the registry is re-parsed.
   */
  caseId: z.string().optional(),
});

/** Légifrance article/text — opened in a new tab. URL is rebuilt by code from `legiId`, never by the LLM. */
export const LegifranceCitationSchema = z.object({
  kind: z.literal('legifrance'),
  /** Stable Légifrance id (`LEGIARTI…` / `LEGITEXT…`). */
  legiId: z.string(),
  url: z.string(),
  name: z.string(),
});

/**
 * Jurisprudence (judiciaire OR administratif) — opened in the internal decision
 * viewer via `(ordre, decisionId)`. Decisions come from our own corpus
 * (`jp_decisions` / `admin_decisions`), so `(ordre, decisionId)` is the only
 * identifier present on 100% of decisions, regardless of source.
 */
export const JpCitationSchema = z.object({
  kind: z.literal('jp'),
  ordre: OrdreSchema,
  /** Corpus UUID — primary key in `jp_decisions` / `admin_decisions`. */
  decisionId: z.string(),
  name: z.string(),
});

/** Arbitrary web source (doctrine, web search/fetch) — only a URL exists, no stable business id. */
export const WebCitationSchema = z.object({
  kind: z.literal('web'),
  url: z.string(),
  name: z.string(),
});

/** The resolved destination of a citation token. */
export const CitationTargetSchema = z.discriminatedUnion('kind', [
  PieceCitationSchema,
  LegifranceCitationSchema,
  JpCitationSchema,
  WebCitationSchema,
]);
export type CitationTarget = z.infer<typeof CitationTargetSchema>;

/**
 * `token → target` map for one conversation. Persisted alongside the
 * conversation and delivered to the frontend (REST history + live WS) so a
 * `<hector-link ref="…" />` can be resolved at render time.
 *
 * Read-tolerant by design: parse defensively when rehydrating from JSONB — an
 * entry that fails validation is dropped, not fatal (the token then renders as
 * plain text, same as an unknown token).
 */
export const CitationRegistrySchema = z.record(z.string(), CitationTargetSchema);
export type CitationRegistry = z.infer<typeof CitationRegistrySchema>;
